import React, { useEffect, useRef } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Easing,
} from 'react-native';
import {
  Text,
  Card,
  ProgressBar,
  useTheme,
  Chip,
  IconButton,
} from 'react-native-paper';
import type { MasteryState } from '@coffeeplexity/shared';
import { ConfidenceIndicator } from './ConfidenceIndicator';

interface SkillMasteryDisplayProps {
  masteryState: MasteryState;
  onPress?: () => void;
  animationEnabled?: boolean;
  compact?: boolean;
  showInsights?: boolean;
  showTrend?: boolean;
  style?: any;
}

export function SkillMasteryDisplay({
  masteryState,
  onPress,
  animationEnabled = true,
  compact = false,
  showInsights = true,
  showTrend = true,
  style,
}: SkillMasteryDisplayProps) {
  const theme = useTheme();
  const progressAnimation = useRef(new Animated.Value(0)).current;
  const scaleAnimation = useRef(new Animated.Value(1)).current;
  const glowAnimation = useRef(new Animated.Value(0)).current;

  // Animate progress bar on mount or when mastery level changes
  useEffect(() => {
    if (animationEnabled) {
      Animated.timing(progressAnimation, {
        toValue: masteryState.masteryLevel,
        duration: 1000,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }).start();

      // Add glow effect for improving skills
      if (masteryState.trend === 'improving') {
        Animated.loop(
          Animated.sequence([
            Animated.timing(glowAnimation, {
              toValue: 1,
              duration: 1500,
              useNativeDriver: false,
            }),
            Animated.timing(glowAnimation, {
              toValue: 0,
              duration: 1500,
              useNativeDriver: false,
            }),
          ])
        ).start();
      }
    } else {
      progressAnimation.setValue(masteryState.masteryLevel);
    }
  }, [masteryState.masteryLevel, masteryState.trend, animationEnabled]);

  const handlePress = () => {
    if (onPress) {
      // Add press animation
      Animated.sequence([
        Animated.timing(scaleAnimation, {
          toValue: 0.95,
          duration: 100,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnimation, {
          toValue: 1,
          duration: 100,
          useNativeDriver: true,
        }),
      ]).start();

      onPress();
    }
  };

  const getMasteryLevelInfo = (level: number) => {
    if (level >= 0.9) return { label: 'Expert', color: '#1B5E20', icon: '🏆' };
    if (level >= 0.8)
      return { label: 'Advanced', color: '#2E7D32', icon: '⭐' };
    if (level >= 0.6)
      return { label: 'Proficient', color: '#388E3C', icon: '✅' };
    if (level >= 0.4)
      return { label: 'Intermediate', color: '#FBC02D', icon: '📈' };
    if (level >= 0.2)
      return { label: 'Beginner', color: '#F57C00', icon: '🌱' };
    return { label: 'Novice', color: '#D32F2F', icon: '🎯' };
  };

  const getTrendInfo = (trend: string) => {
    switch (trend) {
      case 'improving':
        return { icon: '↗️', color: theme.colors.primary, label: 'Improving' };
      case 'declining':
        return {
          icon: '↘️',
          color: theme.colors.error,
          label: 'Needs attention',
        };
      default:
        return {
          icon: '➡️',
          color: theme.colors.onSurfaceVariant,
          label: 'Stable',
        };
    }
  };

  const masteryInfo = getMasteryLevelInfo(masteryState.masteryLevel);
  const trendInfo = getTrendInfo(masteryState.trend);

  const glowColor = glowAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ['rgba(76, 175, 80, 0)', 'rgba(76, 175, 80, 0.3)'],
  });

  if (compact) {
    return (
      <Animated.View
        style={[
          { transform: [{ scale: scaleAnimation }] },
          animationEnabled &&
            masteryState.trend === 'improving' && {
              shadowColor: glowColor,
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 1,
              shadowRadius: 8,
            },
        ]}
      >
        <TouchableOpacity onPress={handlePress} disabled={!onPress}>
          <Card style={[styles.compactCard, style]} mode='outlined'>
            <Card.Content style={styles.compactContent}>
              {/* Skill Name and Level */}
              <View style={styles.compactHeader}>
                <Text
                  variant='bodyMedium'
                  style={styles.compactSkillName}
                  numberOfLines={1}
                >
                  {masteryState.skillName}
                </Text>
                <Text
                  variant='bodySmall'
                  style={[styles.levelBadge, { color: masteryInfo.color }]}
                >
                  {masteryInfo.icon}
                </Text>
              </View>

              {/* Progress Bar */}
              <View style={styles.compactProgressContainer}>
                <Animated.View style={styles.progressWrapper}>
                  <ProgressBar
                    progress={
                      animationEnabled
                        ? progressAnimation
                        : masteryState.masteryLevel
                    }
                    color={masteryInfo.color}
                    style={styles.compactProgressBar}
                  />
                </Animated.View>
                <Text variant='bodySmall' style={styles.compactPercentage}>
                  {Math.round(masteryState.masteryLevel * 100)}%
                </Text>
              </View>

              {/* Trend Indicator */}
              {showTrend && (
                <View style={styles.compactTrend}>
                  <Text style={[styles.trendIcon, { color: trendInfo.color }]}>
                    {trendInfo.icon}
                  </Text>
                </View>
              )}
            </Card.Content>
          </Card>
        </TouchableOpacity>
      </Animated.View>
    );
  }

  return (
    <Animated.View
      style={[
        { transform: [{ scale: scaleAnimation }] },
        animationEnabled &&
          masteryState.trend === 'improving' && {
            shadowColor: glowColor,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 1,
            shadowRadius: 12,
          },
      ]}
    >
      <TouchableOpacity onPress={handlePress} disabled={!onPress}>
        <Card style={[styles.fullCard, style]} mode='outlined'>
          <Card.Content style={styles.fullContent}>
            {/* Header */}
            <View style={styles.header}>
              <View style={styles.skillInfo}>
                <Text variant='titleMedium' style={styles.skillName}>
                  {masteryState.skillName}
                </Text>
                <Text variant='bodySmall' style={styles.skillCategory}>
                  {masteryState.skillCategory}
                </Text>
              </View>

              <View style={styles.headerActions}>
                <Chip
                  icon={() => <Text>{masteryInfo.icon}</Text>}
                  style={[
                    styles.levelChip,
                    { backgroundColor: masteryInfo.color + '20' },
                  ]}
                  textStyle={{ color: masteryInfo.color }}
                  compact
                >
                  {masteryInfo.label}
                </Chip>
                {onPress && (
                  <IconButton
                    icon='chevron-right'
                    size={20}
                    iconColor={theme.colors.onSurfaceVariant}
                  />
                )}
              </View>
            </View>

            {/* Progress Section */}
            <View style={styles.progressSection}>
              <View style={styles.progressHeader}>
                <Text variant='bodyMedium' style={styles.progressLabel}>
                  Mastery Level
                </Text>
                <Text
                  variant='bodyMedium'
                  style={[styles.progressValue, { color: masteryInfo.color }]}
                >
                  {Math.round(masteryState.masteryLevel * 100)}%
                </Text>
              </View>

              <Animated.View style={styles.progressWrapper}>
                <ProgressBar
                  progress={
                    animationEnabled
                      ? progressAnimation
                      : masteryState.masteryLevel
                  }
                  color={masteryInfo.color}
                  style={styles.progressBar}
                />
              </Animated.View>

              {/* Confidence Indicator */}
              <View style={styles.confidenceSection}>
                <ConfidenceIndicator
                  confidence={masteryState.confidence}
                  size='small'
                  showLabel={false}
                />
                <Text variant='bodySmall' style={styles.confidenceLabel}>
                  {Math.round(masteryState.confidence * 100)}% confidence
                </Text>
              </View>
            </View>

            {/* Trend and Stats */}
            {showTrend && (
              <View style={styles.trendSection}>
                <View style={styles.trendInfo}>
                  <Text style={[styles.trendIcon, { color: trendInfo.color }]}>
                    {trendInfo.icon}
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={[styles.trendLabel, { color: trendInfo.color }]}
                  >
                    {trendInfo.label}
                  </Text>
                </View>

                <View style={styles.statsInfo}>
                  <Text variant='bodySmall' style={styles.statText}>
                    {masteryState.evidenceCount} evidence points
                  </Text>
                  <Text variant='bodySmall' style={styles.statText}>
                    Updated{' '}
                    {new Date(masteryState.lastUpdated).toLocaleDateString()}
                  </Text>
                </View>
              </View>
            )}

            {/* AI Insights */}
            {showInsights &&
              masteryState.aiInsights &&
              masteryState.aiInsights.length > 0 && (
                <View style={styles.insightsSection}>
                  <Text variant='bodySmall' style={styles.insightsTitle}>
                    AI Insights
                  </Text>
                  {masteryState.aiInsights.slice(0, 2).map((insight, index) => (
                    <Text
                      key={index}
                      variant='bodySmall'
                      style={styles.insightText}
                    >
                      • {insight}
                    </Text>
                  ))}
                </View>
              )}

            {/* Next Milestone */}
            {masteryState.nextMilestone && (
              <View style={styles.milestoneSection}>
                <Text variant='bodySmall' style={styles.milestoneTitle}>
                  Next Milestone
                </Text>
                <Text variant='bodySmall' style={styles.milestoneText}>
                  {masteryState.nextMilestone}
                </Text>
              </View>
            )}
          </Card.Content>
        </Card>
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  // Compact styles
  compactCard: {
    marginVertical: 2,
  },
  compactContent: {
    padding: 12,
  },
  compactHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  compactSkillName: {
    flex: 1,
    fontWeight: '500',
  },
  levelBadge: {
    fontSize: 16,
    marginLeft: 8,
  },
  compactProgressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  compactProgressBar: {
    height: 4,
    borderRadius: 2,
  },
  compactPercentage: {
    minWidth: 35,
    textAlign: 'right',
    fontWeight: '500',
  },
  compactTrend: {
    position: 'absolute',
    top: 8,
    right: 8,
  },

  // Full styles
  fullCard: {
    marginVertical: 4,
  },
  fullContent: {
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  skillInfo: {
    flex: 1,
  },
  skillName: {
    fontWeight: '600',
    marginBottom: 2,
  },
  skillCategory: {
    opacity: 0.7,
    textTransform: 'capitalize',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  levelChip: {
    height: 28,
  },
  progressSection: {
    marginBottom: 12,
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressLabel: {
    fontWeight: '500',
  },
  progressValue: {
    fontWeight: '600',
  },
  progressWrapper: {
    marginBottom: 8,
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
  },
  confidenceSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  confidenceLabel: {
    opacity: 0.7,
  },
  trendSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  trendInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  trendIcon: {
    fontSize: 16,
  },
  trendLabel: {
    fontWeight: '500',
  },
  statsInfo: {
    alignItems: 'flex-end',
  },
  statText: {
    opacity: 0.7,
  },
  insightsSection: {
    marginBottom: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  insightsTitle: {
    fontWeight: '600',
    marginBottom: 6,
    color: '#1976D2',
  },
  insightText: {
    opacity: 0.8,
    marginBottom: 2,
    paddingLeft: 8,
  },
  milestoneSection: {
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  milestoneTitle: {
    fontWeight: '600',
    marginBottom: 4,
    color: '#7B1FA2',
  },
  milestoneText: {
    opacity: 0.8,
    fontStyle: 'italic',
  },
});
